import { Injectable } from "@angular/core";
import { LanguageService } from "../language/language.service";

@Injectable({ providedIn: "root" })
export class HomeDecisionDetailTechnicalI18nService {
  constructor(private readonly lang: LanguageService) {}

  translateTechnicalValue(entryKey: string, value: unknown): string {
    if (value === undefined || value === null || value === "") {
      return this.lang.translate("home.decisionDetail.notAvailable");
    }

    const translated = this.translateTechnicalNode(entryKey, value);

    if (Array.isArray(translated)) {
      return translated.map((item) => String(item)).join(",\n");
    }

    if (translated && typeof translated === "object") {
      return JSON.stringify(translated, null, 2);
    }

    return String(translated);
  }

  isTechnicalValueMultiline(value: unknown): boolean {
    if (Array.isArray(value)) {
      return value.length > 1;
    }

    if (typeof value === "string") {
      return value.includes("\n") || value.length > 72;
    }

    return !!value && typeof value === "object";
  }

  translateTechnicalString(entryKey: string | null | undefined, raw: string): string {
    if (!this.shouldTranslateResponses()) {
      return raw;
    }

    const translatedList = this.translateDelimitedTokens(raw, entryKey);
    if (translatedList) {
      return translatedList;
    }

    const translatedCopy = this.translateDynamicCopy(raw);
    if (translatedCopy) {
      return translatedCopy;
    }

    return this.translateEnumerationToken(raw, entryKey) ?? raw;
  }

  translateDelimitedTokens(raw: string, contextKey: string | null | undefined): string | null {
    const tokens = raw
      .split(",")
      .map((token) => token.trim())
      .filter(Boolean);

    if (tokens.length < 2) {
      return null;
    }

    const translatedTokens = tokens.map((token) => this.translateEnumerationToken(token, contextKey) ?? token);
    return translatedTokens.join(", ");
  }

  translateEnumerationToken(raw: string, contextKey: string | null | undefined): string | null {
    const normalized = this.normalizeKey(raw);
    if (!normalized || !this.shouldTranslateResponses()) {
      return null;
    }

    const context = this.normalizeKey(contextKey);
    const candidates: string[] = [];

    if (context?.includes("action") || context?.includes("catalog_action")) {
      candidates.push(`home.decisionDetail.action.label.${normalized}`);
    }

    if (context?.includes("role")) {
      candidates.push(`home.decisionDetail.role.${normalized}`);
    }

    if (context?.includes("source")) {
      candidates.push(`home.decisionDetail.source.${normalized}`);
    }

    if (context?.includes("state")) {
      candidates.push(`home.decisionStates.${normalized}`);
    }

    candidates.push(
      `home.decisionDetail.action.label.${normalized}`,
      `home.decisionDetail.role.${normalized}`,
      `home.decisionDetail.source.${normalized}`,
      `home.decisionDetail.evidenceType.${normalized}`,
      `home.decisionDetail.enum.${normalized}`,
      `home.decisionStates.${normalized}`
    );

    for (const candidate of candidates) {
      const translated = this.translateKnownKey(candidate);
      if (translated) {
        return translated;
      }
    }

    return null;
  }

  private translateTechnicalNode(entryKey: string, value: unknown): unknown {
    if (Array.isArray(value)) {
      return value.map((item) => this.translateTechnicalNode(entryKey, item));
    }

    if (typeof value === "string") {
      return this.translateTechnicalString(entryKey, value);
    }

    if (typeof value === "number") {
      return Number.isInteger(value) ? `${value}` : value.toFixed(2);
    }

    if (typeof value === "boolean") {
      return value ? "true" : "false";
    }

    if (value && typeof value === "object") {
      return Object.fromEntries(
        Object.entries(value).map(([nestedKey, nestedValue]) => [
          nestedKey,
          this.translateTechnicalNode(`${entryKey}.${nestedKey}`, nestedValue)
        ])
      );
    }

    return value;
  }

  private translateDynamicCopy(raw?: string | null): string | null {
    if (!raw || !this.shouldTranslateResponses()) {
      return null;
    }

    const normalized = this.normalizeKey(raw);
    if (!normalized) {
      return null;
    }

    return this.translateKnownKey(`home.decisionDetail.dynamic.copy.${normalized}`);
  }

  private translateKnownKey(key: string): string | null {
    const translated = this.lang.translate(key);
    if (translated === key) {
      return null;
    }

    return translated;
  }

  private shouldTranslateResponses(): boolean {
    return this.lang.getCurrentLang() !== "en";
  }

  private normalizeKey(value?: string | null): string | null {
    const trimmed = value?.trim();
    if (!trimmed) {
      return null;
    }

    return trimmed
      .replace(/([a-z0-9])([A-Z])/g, "$1_$2")
      .normalize("NFKD")
      .replace(/[\u0300-\u036f]/g, "")
      .replace(/[^a-zA-Z0-9]+/g, "_")
      .replace(/^_+|_+$/g, "")
      .toLowerCase();
  }
}
